import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { localizationAPI } from '../services/api';
import { useAuth } from './AuthContext';

const STORAGE_KEY = 'app_language';

const DEFAULT_EN = {
  app_name: 'Daily Tracker',
};

const LocalizationContext = createContext(null);

export const LocalizationProvider = ({ children }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState({ en: DEFAULT_EN });
  const [defaultLanguage, setDefaultLanguage] = useState('en');
  const [currentLanguage, setCurrentLanguageState] = useState(() => localStorage.getItem(STORAGE_KEY) || 'en');

  const applyPayload = useCallback((payload) => {
    const nextLanguages = Array.isArray(payload?.languages) ? payload.languages : [];
    const nextDefault = payload?.defaultLanguage || 'en';
    const nextTranslations = payload?.translations || {};

    setLanguages(nextLanguages);
    setDefaultLanguage(nextDefault);
    setTranslations((prev) => ({
      ...prev,
      ...nextTranslations,
      en: {
        ...DEFAULT_EN,
        ...(nextTranslations.en || prev.en || {}),
      },
    }));

    const isCurrentActive = nextLanguages.some((lang) => lang.code === currentLanguage && lang.isActive);
    if (!isCurrentActive) {
      setCurrentLanguageState(nextDefault);
      localStorage.setItem(STORAGE_KEY, nextDefault);
    }
  }, [currentLanguage]);

  const refreshLocalization = useCallback(async () => {
    try {
      setLoading(true);
      const response = await localizationAPI.getAll();
      applyPayload(response.data);
    } catch (error) {
      setTranslations((prev) => ({
        ...prev,
        en: {
          ...DEFAULT_EN,
          ...(prev.en || {}),
        },
      }));
    } finally {
      setLoading(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    refreshLocalization();
  }, [refreshLocalization]);

  useEffect(() => {
    if (!token) return;
    refreshLocalization();
  }, [token, refreshLocalization]);

  const setCurrentLanguage = useCallback((code) => {
    setCurrentLanguageState(code);
    localStorage.setItem(STORAGE_KEY, code);
  }, []);

  const t = useCallback((key, fallback = '') => {
    const current = translations[currentLanguage] || {};
    const def = translations[defaultLanguage] || translations.en || {};

    if (Object.prototype.hasOwnProperty.call(current, key) && current[key] !== '') {
      return current[key];
    }

    if (Object.prototype.hasOwnProperty.call(def, key) && def[key] !== '') {
      return def[key];
    }

    return fallback || key;
  }, [currentLanguage, defaultLanguage, translations]);

  const value = useMemo(() => ({
    loading,
    languages,
    translations,
    defaultLanguage,
    currentLanguage,
    t,
    setCurrentLanguage,
    refreshLocalization,
    setLocalizationPayload: applyPayload,
  }), [
    applyPayload,
    currentLanguage,
    defaultLanguage,
    languages,
    loading,
    refreshLocalization,
    setCurrentLanguage,
    t,
    translations,
  ]);

  return (
    <LocalizationContext.Provider value={value}>
      {children}
    </LocalizationContext.Provider>
  );
};

export const useLocalization = () => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error('useLocalization must be used within LocalizationProvider');
  }
  return context;
};
